import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { Tool } from '../Tool';

import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import WebSocketClient from 'js/src/libs/websocket-client';
import { isErrorObject } from 'js/src/types';

export const sketchResearchSchema = z.object({
    research_topic: z.string().describe('The topic to research for video content creation'),
});
export type SketchResearchArgs = z.infer<typeof sketchResearchSchema>;

interface SketchResearchResponse {
    research_content: string;
    research_areas: Array<Record<string, unknown>>;
    usage?: Record<string, unknown>;
    processing_time_seconds?: number;
}

export class SketchResearchTool extends Tool<typeof sketchResearchSchema> {
    protected override readonly schema = sketchResearchSchema;
    protected override readonly toolName = TOOL_NAMES.researchTopic;
    protected override readonly toolDescription =
        'Research a topic using web search and gather relevant information to help create video content';

    public override canRunAsync = true;

    public override getToolDisplayName(): string {
        return 'Searching the web';
    }

    public get statusIcon(): string {
        return 'travel_explore';
    }

    public async execute(args: SketchResearchArgs) {
        const { research_topic } = args;

        try {
            const response = await ApiService.sketchResearchCreate({
                body: {
                    research_topic: research_topic,
                },
            });

            const ws = WebSocketClient.getInstance();
            const result = await ws.waitForMessage<SketchResearchResponse>(
                response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'sketch_research',
            );

            if (!result.research_content) {
                return `No research content could be gathered for topic: "${research_topic}".`;
            }

            return `${result.research_content}\n\n`;
        } catch (error) {
            const errorMessage = isErrorObject(error)
                ? error.error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error occurred';
            return `Error researching topic: ${errorMessage}`;
        }
    }
}
